/**
 * Organization Store — manages current org selection
 * Org ID is stored in a non-HttpOnly cookie so the server-side hub proxy can read it.
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type { OrgWithRole, OrgRole } from "@/types/organization.types";
import { organizationService } from "@/lib/api/services/organization.service";
import { setOrgCookie, getOrgCookie, clearOrgCookie } from "@/lib/auth/org-cookies";
import { DEFAULT_ENABLED_FEATURES } from "@/features/system-admin/constants/features";

interface OrgState {
  organizations: OrgWithRole[];
  currentOrg: OrgWithRole | null;
  isLoading: boolean;
  isInitialized: boolean;
}

interface OrgActions {
  fetchOrganizations: () => Promise<OrgWithRole[]>;
  selectOrg: (orgId: string) => void;
  clearOrg: () => void;
  hydrate: () => Promise<void>;
}

type OrgStore = OrgState & OrgActions;

function normalizeOrg(org: OrgWithRole): OrgWithRole {
  return {
    ...org,
    enabledFeatures: Array.isArray(org.enabledFeatures)
      ? org.enabledFeatures
      : [...DEFAULT_ENABLED_FEATURES],
  };
}

function normalizeOrgs(orgs: OrgWithRole[]): OrgWithRole[] {
  return orgs.map(normalizeOrg);
}

export function isUsableOrg(org: Pick<OrgWithRole, "status">): boolean {
  return !org.status || org.status === "active";
}

export const useOrgStore = create<OrgStore>((set, get) => ({
  organizations: [],
  currentOrg: null,
  isLoading: false,
  isInitialized: false,

  fetchOrganizations: async () => {
    set({ isLoading: true });
    try {
      const response = await organizationService.getMyOrganizations();
      const orgs = normalizeOrgs(response.data || []);
      set({ organizations: orgs, isLoading: false });
      return orgs;
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      // A transient refresh failure must not erase a valid org context. This
      // path is used immediately after accepting an invitation; clearing the
      // list would incorrectly render the zero-org/create state even though
      // the membership transition already committed.
      const organizations = get().organizations;
      set({ isLoading: false });
      return organizations;
    }
  },

  selectOrg: (orgId: string) => {
    const { organizations } = get();
    const org = organizations.find((o) => o.id === orgId) || null;
    if (org && isUsableOrg(org)) {
      setOrgCookie(orgId);
      set({ currentOrg: org });
    } else if (org) {
      clearOrgCookie();
      set({ currentOrg: null });
    }
  },

  clearOrg: () => {
    clearOrgCookie();
    set({ currentOrg: null, organizations: [], isInitialized: false });
  },

  hydrate: async () => {
    // Guard: only hydrate once — prevents duplicate API calls
    // when multiple components (OrgGuard, SelectOrgClient) call hydrate
    const { isInitialized, isLoading } = get();
    if (isInitialized || isLoading) return;

    set({ isLoading: true });

    const savedOrgId = getOrgCookie();

    try {
      const response = await organizationService.getMyOrganizations();
      const orgs = normalizeOrgs(response.data || []);

      let selectedOrg: OrgWithRole | null = null;

      // Try to restore saved org from cookie
      if (savedOrgId && orgs.length > 0) {
        const found = orgs.find((o) => o.id === savedOrgId);
        if (found && isUsableOrg(found)) {
          selectedOrg = found;
        } else {
          // Saved org no longer exists or is not active enough to enter.
          clearOrgCookie();
        }
      }

      // Auto-select only active orgs. Pending/suspended orgs must land on
      // select-org/awaiting-approval instead of becoming the request context.
      const usableOrgs = orgs.filter(isUsableOrg);
      if (!selectedOrg && usableOrgs.length === 1) {
        selectedOrg = usableOrgs[0];
        setOrgCookie(usableOrgs[0].id);
      }

      set({
        organizations: orgs,
        currentOrg: selectedOrg,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      console.error("Failed to hydrate org store:", error);
      set({
        organizations: [],
        currentOrg: null,
        isLoading: false,
        isInitialized: true,
      });
    }
  },
}));

// ── Selectors ──

export const useOrg = () =>
  useOrgStore(
    useShallow((s) => ({
      currentOrg: s.currentOrg,
      organizations: s.organizations,
      isLoading: s.isLoading,
      isInitialized: s.isInitialized,
    })),
  );

export const useOrgActions = () =>
  useOrgStore(
    useShallow((s) => ({
      fetchOrganizations: s.fetchOrganizations,
      selectOrg: s.selectOrg,
      clearOrg: s.clearOrg,
      hydrate: s.hydrate,
    })),
  );

export const useCurrentOrg = () => useOrgStore((s) => s.currentOrg);
export const useMyOrgRole = (): OrgRole | null => useOrgStore((s) => s.currentOrg?.myRole ?? null);

// True for owner or admin — mirrors backend `middleware.IsOrgAdmin`. Use this
// for `canEdit` flags and admin-gated UI; do NOT use raw `=== "admin"`
// because owner is the higher rank and would be wrongly excluded.
export const useIsOrgAdmin = (): boolean =>
  useOrgStore((s) => {
    const role = s.currentOrg?.myRole;
    return role === "owner" || role === "admin";
  });
